import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './components/Header';
import CategorySelector from './components/CategorySelector';
import Navigation from './components/Navigation';
import Overview from './components/Overview';
import Compare from './components/Compare';
import Technical from './components/Technical';
import Selector from './components/Selector';
import Login from './components/Login';
import { API_URL } from './config';
import { useCurrency } from './hooks/useCurrency';
import { setExchangeRates } from './utils/currencyConverter';
import { ShipmentProvider } from './contexts/ShipmentContext';

// Configure axios to use API_URL as base URL
axios.defaults.baseURL = API_URL;

function App() {
  const [currentCategory, setCurrentCategory] = useState('hdeo');
  const [currentView, setCurrentView] = useState('ov');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const {
    selectedCurrency,
    setSelectedCurrency,
    loading: currencyLoading,
    error: currencyError,
    refreshRates,
    formatPrice
    } = useCurrency();

  const { rates } = useCurrency();

  useEffect(() => {
    if (rates) {
      setExchangeRates(rates);
      console.log('Exchange rates synced with converter:', rates);
    }
  }, [rates]);

  // Функция для перехода с деталей продукта на все продукты выбранной категории
  const handleBackToOverview = () => {
    setSelectedProduct(null); // сбрасываем выбранный продукт
    setCurrentView('ov'); // переключаем на Overview
  };

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      // Set axios default authorization header
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
    
    fetchCategories();
    fetchCategoryCounts();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [currentCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories/');
      const data = Array.isArray(response.data) ? response.data : response.data.results || [];
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  // Функция для получения продукта по ссылке
  useEffect(() => {
    // Проверяем URL при загрузке
    const params = new URLSearchParams(window.location.search);
    const productId = params.get('product');

    if (productId) {
      // Ищем продукт по ID и открываем его
      fetchProductById(productId);
    }
  }, []);

const fetchProductById = async (id) => {
console.log('=== DEBUG ===');
  console.log('Requested product ID:', id);
  console.log('Current user authenticated:', !!localStorage.getItem('token'));
  console.log('Token:', localStorage.getItem('token'));
  try {
    const response = await axios.get(`/api/products/${id}/`);
    const product = response.data;
    setSelectedProduct(product);
    setCurrentView('td');
    if (product.category_name) {
      setCurrentCategory(product.category_name);
    }
  } catch (error) {
    console.error('Ошибка загрузки продукта:', error);
  }
};

  // Функция для подсчета продуктов категориях
  const fetchCategoryCounts = async () => {
    try {
      const response = await axios.get('/api/products/count_in_categories/');
      const data = response.data;
      
      const counts = {
        // Для категории hdeo и pcmo отдельный счет
        hdmo: data.hdeo_count || 0,
        pcmo: data.pcmo_count || 0,
        // Добавляем other как сумму продуктов в остальных категориях
        other: data.other_count || 0
      };
      
      setCategoryCounts(counts);
    } catch (error) {
      console.error('Error fetching category counts:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/products/?category__name=${currentCategory}`);
      const data = Array.isArray(response.data) ? response.data : response.data.results || [];
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product) => {
    // Если кликаем на тот же продукт, то скроллим вверх
    if (selectedProduct?.id === product.id) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }

    // Если продукт из другой категории - меняем категорию
    if (product.category_name && product.category_name !== currentCategory) {
      setCurrentCategory(product.category_name);
    }

    setSelectedProduct(product);
    setSelectedSystem(null);
    setCurrentView('td');
  };

  const handleSystemClick = (system) => {
    setSelectedSystem(system);
    setSelectedProduct(null);
    setCurrentView('td');
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setShowLoginModal(false);
    const token = localStorage.getItem('token');
    if (token) {
      // Set axios default authorization header
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
      // Refresh products to get user's filtered list
      fetchProducts();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
    // Refresh products to show all products again
    fetchProducts();
  };

  const handleCategoryChange = (category) => {
    setCurrentCategory(category);
    setCurrentView('ov');
  };

  const renderView = () => {
    switch (currentView) {
      case 'ov':
        return <Overview 
          products={products} 
          category={currentCategory} 
          loading={loading} 
          onProductClick={handleProductClick}
          onSystemClick={handleSystemClick}
          isAuthenticated={isAuthenticated}
          currency={selectedCurrency}
          formatPrice={formatPrice}
          currencyLoading={currencyLoading}
        />;
      case 'cm':
        return <Compare 
          products={products} 
          category={currentCategory} 
          loading={loading}
          isAuthenticated={isAuthenticated}
        />;
      case 'td':
        return <Technical 
          products={products} 
          category={currentCategory} 
          loading={loading} 
          selectedProduct={selectedProduct} 
          onProductSelect={setSelectedProduct}
          selectedSystem={selectedSystem}
          onSystemSelect={setSelectedSystem}
          isAuthenticated={isAuthenticated}
          onProductClick={handleProductClick}
          onCategoryChange={handleCategoryChange}
          onBackToCategory={handleBackToOverview}
          currency={selectedCurrency}
          formatPrice={formatPrice}
          currencyLoading={currencyLoading}
        />;
      case 'sl':
        return <Selector 
          products={products} 
          category={currentCategory} 
          loading={loading}
          isAuthenticated={isAuthenticated}
        />;
      default:
        return <Overview 
          products={products} 
          category={currentCategory} 
          loading={loading} 
          onProductClick={handleProductClick}
          onSystemClick={handleSystemClick}
          isAuthenticated={isAuthenticated}
          currency={selectedCurrency}
          formatPrice={formatPrice}
          currencyLoading={currencyLoading}
        />;
    }
  };

  return (
  <ShipmentProvider>
    <div className="App">
      {showLoginModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowLoginModal(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <Login onLogin={handleLogin} />
          </div>
        </div>
      )}
      
      <Header 
        categoryCounts={categoryCounts}
        user={user}
        onLogout={handleLogout}
        onLoginClick={() => setShowLoginModal(true)}
        isAuthenticated={isAuthenticated}
        selectedCurrency={selectedCurrency}
        setSelectedCurrency={setSelectedCurrency}
        currencyLoading={currencyLoading}
        currencyError={currencyError}
        refreshRates={refreshRates}
        formatPrice={formatPrice}
      />
      <div className="W">
        <CategorySelector 
          categories={categories}
          currentCategory={currentCategory}
          onCategoryChange={handleCategoryChange}
        />
        <Navigation 
          currentView={currentView}
          onViewChange={setCurrentView}
        />
        {renderView()}
      </div>
    </div>
    </ShipmentProvider>
  );
}

export default App;
